import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GraduationCap, Mail, Lock, User, ArrowLeft, KeyRound, RefreshCw, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cloudflare Turnstile Component
function TurnstileWidget({ onVerify, onError, resetKey }) {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const [siteKey, setSiteKey] = useState(null);

  useEffect(() => {
    // Cargar site key desde el backend
    axios.get(`${API}/api/auth/turnstile-key`)
      .then(res => setSiteKey(res.data.site_key))
      .catch(() => console.error('Error cargando Turnstile key'));
  }, []);

  useEffect(() => {
    if (!siteKey) return;

    // Cargar script de Turnstile si no existe
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => renderWidget();
      document.head.appendChild(script);
    } else {
      renderWidget();
    }

    function renderWidget() {
      if (containerRef.current && window.turnstile) {
        // Limpiar widget anterior si existe
        if (widgetId.current !== null) {
          try { window.turnstile.remove(widgetId.current); } catch {}
        }
        
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerify(token),
          'error-callback': () => onError && onError(),
          theme: 'light',
          size: 'normal'
        });
      }
    }

    return () => {
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch {}
      }
    };
  }, [siteKey, onVerify, onError, resetKey]);

  if (!siteKey) {
    return (
      <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg">
        <RefreshCw className="w-4 h-4 animate-spin text-slate-400 mr-2" />
        <span className="text-sm text-slate-500">Cargando verificación...</span>
      </div>
    );
  }

  return <div ref={containerRef} className="flex justify-center"></div>;
}

export default function AcademiasLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login, register, verify, forgot, forgot-sent
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState('');
  
  // CAPTCHA state
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileResetKey(prev => prev + 1);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      // Guardar token en localStorage como backup para Safari
      if (response.data?.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      toast.success('Bienvenido');
      // Use window.location.replace to force a full page reload and re-check session
      window.location.replace('/academias/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al iniciar sesión';
      if (detail.includes('no verificado')) {
        toast.info('Revisa tu email para el código de verificación');
        setMode('verify');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (!turnstileToken) {
      toast.error('Por favor completa la verificación de seguridad');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/register`, { 
        email, 
        password, 
        nombre,
        turnstile_token: turnstileToken
      });
      toast.success('Registro exitoso. Revisa tu email para el código');
      setMode('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/verificar-codigo`, { email, codigo }, { withCredentials: true });
      toast.success('Email verificado. Bienvenido!');
      // Use window.location.replace to force a full page reload and re-check session
      window.location.replace('/academias/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/api/auth/reenviar-codigo`, { email });
      toast.success('Código reenviado');
    } catch (error) {
      toast.error('Error al reenviar código');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Ingresa tu correo electrónico');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/solicitar-recuperacion`, { email });
      toast.success('Si el email existe, recibirás instrucciones');
      setMode('forgot-sent');
    } catch (error) {
      toast.error('Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4 text-slate-400 hover:text-white"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al inicio
        </Button>

        <Card className="p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Academias Deportivas
            </h1>
            <p className="text-slate-600 text-sm">
              {mode === 'login' && 'Ingresa con tu cuenta'}
              {mode === 'register' && 'Crea tu cuenta gratis'}
              {mode === 'verify' && 'Verifica tu email'}
              {mode === 'forgot' && 'Recupera tu contraseña'}
              {mode === 'forgot-sent' && 'Revisa tu correo'}
            </p>
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                    placeholder="tu@email.com"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Ingresando...' : 'Ingresar'}
              </Button>

              <button 
                type="button" 
                onClick={() => setMode('forgot')} 
                className="w-full text-sm text-slate-500 hover:text-emerald-600"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>

              <p className="text-center text-sm text-slate-600 mt-4">
                ¿No tienes cuenta?{' '}
                <button 
                  type="button" 
                  onClick={() => setMode('register')} 
                  className="text-emerald-600 hover:underline font-medium"
                  data-testid="register-link"
                >
                  Regístrate gratis
                </button>
              </p>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="nombre">Nombre completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="nombre"
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    required
                    className="pl-10"
                    placeholder="Tu nombre"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                    placeholder="tu@email.com"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10"
                    placeholder="Mínimo 6 caracteres"
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              {/* Cloudflare Turnstile CAPTCHA */}
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">Verificación de seguridad</span>
                </div>
                <TurnstileWidget 
                  onVerify={(token) => setTurnstileToken(token)}
                  onError={() => toast.error('Error en la verificación')}
                  resetKey={turnstileResetKey}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={loading || !turnstileToken}
                data-testid="register-submit-btn"
              >
                {loading ? 'Registrando...' : 'Crear cuenta'}
              </Button>

              <p className="text-center text-sm text-slate-600 mt-4">
                ¿Ya tienes cuenta?{' '}
                <button 
                  type="button" 
                  onClick={() => { setMode('login'); resetTurnstile(); }} 
                  className="text-emerald-600 hover:underline font-medium"
                  data-testid="login-link"
                >
                  Inicia sesión
                </button>
              </p>
            </form>
          )}

          {/* Verify Form */}
          {mode === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="verify-form">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <KeyRound className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-600">
                  Enviamos un código de 6 dígitos a<br />
                  <span className="font-medium text-slate-900">{email}</span>
                </p>
              </div>

              <div>
                <Label htmlFor="codigo">Código de verificación</Label>
                <Input
                  id="codigo"
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  data-testid="verify-code-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={loading || codigo.length !== 6}
                data-testid="verify-submit-btn"
              >
                {loading ? 'Verificando...' : 'Verificar'}
              </Button>

              <div className="text-center">
                <button 
                  type="button" 
                  onClick={handleResend} 
                  className="text-sm text-slate-500 hover:text-emerald-600"
                  data-testid="resend-code-btn"
                >
                  ¿No recibiste el código? Reenviar
                </button>
              </div>

              <button 
                type="button" 
                onClick={() => setMode('login')} 
                className="w-full text-sm text-slate-500 hover:text-slate-700"
                data-testid="back-to-login-btn"
              >
                Volver al inicio de sesión
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-600">
                  Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña
                </p>
              </div>

              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                    placeholder="tu@email.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700" 
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Enviando...' : 'Enviar enlace'}
              </Button>

              <button 
                type="button" 
                onClick={() => setMode('login')} 
                className="w-full text-sm text-slate-500 hover:text-slate-700"
                data-testid="back-to-login-forgot-btn"
              >
                Volver al inicio de sesión
              </button>
            </form>
          )}

          {/* Forgot Password Sent */}
          {mode === 'forgot-sent' && (
            <div className="text-center space-y-4" data-testid="forgot-sent-view">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">¡Correo enviado!</h3>
              <p className="text-sm text-slate-600">
                Si existe una cuenta con el correo <span className="font-medium">{email}</span>, 
                recibirás un enlace para restablecer tu contraseña.
              </p>
              <p className="text-xs text-slate-500">
                Revisa también tu carpeta de spam si no lo encuentras.
              </p>
              <Button 
                onClick={() => setMode('login')} 
                variant="outline" 
                className="w-full mt-4"
                data-testid="back-to-login-sent-btn"
              >
                Volver al inicio de sesión
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
